import {
  Box,
  Button,
  Grid,
  IconButtonProps,
  List,
  Stack,
  TablePagination,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";

import { useImageQuery } from "api/services/useImageQuery";
import { useLabels } from "api/services/useLabels";
import { STATUS } from "components/detector/Activity";
import { NotesModal } from "components/detector/NotesModal";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { useQueryDetailsHotkeys } from "hooks/useHotkeys/useHotkeys";
import { ConfirmDeleteModal } from "new-components/modals/ConfirmDeleteModal";
import { Modal } from "new-components/modals/Modal";
import { Typography } from "new-components/typography/Typography";
import {
  SET_CURRENT_INDEX,
  SET_IMAGE_QUERY,
  SET_LOADING,
  queryDetailsModalReducer,
} from "reducers/QueryDetailsModalReducer";
import { setIsUnsaveModalOpen, unsavedAnnotations } from "store/slices/FocusModal/focusModalSlice";
import { ListResponse } from "types/api";
import { Detector } from "types/detectors";
import { ImageQuery } from "types/imageQueries";

import { useInitialStep } from "./components/FirstUseOfQueryDetails";
import { ImageQueryPanel } from "./components/ImageQueryPanel";
import { QueryDetailsPreview } from "./components/QueryDetailsPreview";
import { ModalStep } from "./types";

export interface QueryDetailsModalProps {
  loading: boolean;
  getData: (() => Promise<void>) | ((route?: string | undefined) => Promise<ListResponse<ImageQuery>>);
  rows: ImageQuery[];
  rowCount: number;
  selectedRowIndex: number;
  setSelectedRowIndex?: React.Dispatch<React.SetStateAction<number>>;
  showDialog: boolean;
  onClose: () => void;
  page: number;
  pageSize: number;
  setPageSize?: (pageSize: number) => void;
  setPage?: (page: number) => void;
  mode?: string;
  detector: Detector;
  onDeleteRefetch?: () => Promise<void>;
  owner?: {
    id: string;
    name: string;
  };
}

export const QueryDetailsModal = React.memo((props: QueryDetailsModalProps) => {
  const {
    getData,
    loading,
    mode,
    onClose,
    page,
    pageSize,
    rowCount,
    rows,
    selectedRowIndex,
    setSelectedRowIndex,
    setPage,
    setPageSize,
    showDialog,
    detector,
    onDeleteRefetch,
    owner,
  } = props;

  const theme = useTheme();
  const isSM = useMediaQuery(theme.breakpoints.down("md"));

  const [{ posicheck, loadingLabels }, localDispatch] = useReducer(queryDetailsModalReducer, {
    posicheck: undefined,
    loadingLabels: false,
  });

  interface AnnotationsHoldReason {
    newIndex: number;
    newPage: number;
    isClosing: boolean;
  }

  const ANNOTATIONS_KEEP_REASON_DEFAULT: AnnotationsHoldReason = { newIndex: -1, newPage: -1, isClosing: false };

  const [annotationsHoldReason, setAnnotationsHoldReason] = useState<AnnotationsHoldReason>(
    ANNOTATIONS_KEEP_REASON_DEFAULT
  );
  // const [loadingPage, setLoadingPage] = useState<string>("");
  const [isRelabeling, setIsReLabeling] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);

  const unsavedAnnotation = useAppSelector(unsavedAnnotations);
  const dispatch = useAppDispatch();
  const updateCounter = useRef(0);

  const {
    imageQuery: currentImageQueryData,
    getImageQuery: getCurrentImageQuery,
    deleteImageQuery: deleteIQ,
    isDeleting: isLoadingIQDelete,
  } = useImageQuery(posicheck?.iq_id);

  // image query labels
  const { labels: dataLabels, getLabels: getDataLabels } = useLabels(posicheck?.iq_id);

  useEffect(() => {
    localDispatch({ type: SET_CURRENT_INDEX, payload: selectedRowIndex });
  }, [selectedRowIndex]);

  useEffect(() => {
    if (selectedRowIndex !== null && rows?.length > 0) {
      localDispatch({ type: SET_IMAGE_QUERY, payload: rows[selectedRowIndex] });
    }
  }, [selectedRowIndex, rows]);

  useEffect(() => {
    if (mode === STATUS.FLAGGED && posicheck && Object.keys(posicheck).length !== 0) {
      getCurrentImageQuery();
      updateCounter.current = 0;
    }
  }, [getCurrentImageQuery, mode, posicheck]);

  useEffect(() => {
    let isMounted = true;
    if (posicheck && Object.keys(posicheck).length !== 0) {
      localDispatch({ type: SET_LOADING, payload: true });
      getDataLabels().finally(() => {
        if (isMounted) {
          localDispatch({ type: SET_LOADING, payload: false });
        }
      });
    }
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getDataLabels, posicheck, localDispatch]);

  useEffect(() => {
    let id: number;
    if (mode === STATUS.FLAGGED) {
      id = window.setInterval(() => {
        updateCounter.current += 1;

        if (updateCounter.current >= 10) {
          getDataLabels();
          getCurrentImageQuery();

          updateCounter.current = 0;
        }
      }, 1000);
    }
    return () => clearInterval(id);
  }, [getCurrentImageQuery, mode, posicheck, getDataLabels]);

  /**
   * Closes query details modal when there is no rows to show after finishes loading.
   */
  useEffect(() => {
    if (rows.length === 0 && !loading) {
      onClose();
    }
  }, [rows, onClose, loading]);

  const handleAnnotationsHoldReasonNewIndex = (newIndex: number) =>
    setAnnotationsHoldReason((prev) => ({ ...prev, newIndex }));

  const checkAnnotationsHoldReason = () => {
    const { newIndex, newPage, isClosing } = annotationsHoldReason;

    // Handle page change with or without index change
    if (newPage >= 0 && setPage) {
      setPage(newPage);
      if (newIndex >= 0 && setSelectedRowIndex) {
        setSelectedRowIndex(newIndex);
      }
      setAnnotationsHoldReason(ANNOTATIONS_KEEP_REASON_DEFAULT);
      return;
    }

    // Handle index change within same page
    if (newIndex >= 0 && setSelectedRowIndex) {
      setSelectedRowIndex(newIndex);
      setAnnotationsHoldReason(ANNOTATIONS_KEEP_REASON_DEFAULT);
    }

    if (isClosing) {
      onClose();
    }
  };

  const updatedIQ = useMemo(() => currentImageQueryData || posicheck, [currentImageQueryData, posicheck]);

  const handleChangePage = (_: any, newPage: number, targetIndex: number = 0) => {
    if (unsavedAnnotation) {
      setAnnotationsHoldReason((prev) => ({ ...prev, newPage: newPage + 1, newIndex: targetIndex }));
      dispatch(setIsUnsaveModalOpen(true));
      return;
    }
    if (setPage) {
      setPage(newPage + 1);
      if (setSelectedRowIndex) setSelectedRowIndex(targetIndex);
    }
  };

  const queryNumber = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    const value = startIndex + selectedRowIndex + 1;
    return value;
  }, [selectedRowIndex, page, pageSize]);

  const getIndex = useCallback((selectedData) => rows.findIndex((row) => row?.iq_id === selectedData?.iq_id), [rows]);

  const updateSelectedRowIndex = (index: number) => {
    if (unsavedAnnotation && index !== selectedRowIndex) {
      handleAnnotationsHoldReasonNewIndex(index);
      dispatch(setIsUnsaveModalOpen(true));
      return;
    }
    if (setSelectedRowIndex) {
      setSelectedRowIndex(index);
    }
  };

  const updateSelectedRowData = (selectedData: ImageQuery) => {
    updateSelectedRowIndex(getIndex(selectedData));
  };

  const onSendLabel = useCallback(async () => {
    try {
      setIsReLabeling(true);
      if (mode === STATUS.FLAGGED) {
        await getCurrentImageQuery();
      } else {
        await getData();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsReLabeling(false);
    }
  }, [getCurrentImageQuery, getData, mode]);

  const onDeleteIQHandler = async () => {
    if (!posicheck?.iq_id) {
      return;
    }

    await deleteIQ(posicheck.iq_id);
    setIsDeleteModalOpen(false);
    if (onDeleteRefetch) onDeleteRefetch();
    else await getData();
    enqueueSnackbar("Image Query successfully deleted!.", {
      variant: "success",
    });
  };

  const handleClose = () => {
    if (unsavedAnnotation) {
      setAnnotationsHoldReason((prev) => ({ ...prev, isClosing: true }));
      dispatch(setIsUnsaveModalOpen(true));
      return;
    }
    onClose();
  };

  const isSinglePage = rowCount <= pageSize;

  const paginationButtonProps: Partial<IconButtonProps> = {
    disabled: loading,
    sx: {
      display: isSinglePage ? "none" : undefined,
    },
  };

  const subtitle = isSM ? updatedIQ?.predictor_name : `${updatedIQ?.predictor_name}:`;
  const [selectedLabelId, setSelectedLabelId] = useState<number | null>(null);
  const labelSelected = !loadingLabels ? dataLabels?.results.find((label) => label.id === selectedLabelId) : null;

  /**
   * Auto-selects the first label on the table.
   *
   * When the image query selected is changed this auto-selects again the first label.
   */
  useEffect(() => {
    const firstLabelId = dataLabels?.results.at(0)?.id ?? null;
    const initialLabelSelected = updatedIQ?.best_prediction_label_id ?? firstLabelId;
    setSelectedLabelId(initialLabelSelected);
  }, [dataLabels?.results, updatedIQ?.best_prediction_label_id]);

  const finalStep: ModalStep = {
    onCLose: handleClose,
    title: isSM ? detector.name : `Query Details: Query ${queryNumber} (of ${String(rowCount)} filtered results)`,
    size: "fullScreen",
    body: (
      <Grid container height="100%" paddingBottom="20px" columnGap="20px">
        <Stack
          direction="row"
          sx={{
            flexShrink: 0,
            height: "50px",
            alignItems: "center",
            width: "100%",
            gap: "8px",
          }}
        >
          <Typography data-testid="detector-name" variant="title" fontWeight="semibold" noWrap>
            {subtitle}
          </Typography>
          <Typography data-testid="query-text" variant="subtitle" className="hidden md:block" noWrap>
            {updatedIQ?.query}
          </Typography>
          <Button
            data-testid="notes-button"
            variant="outlined"
            sx={{
              borderRadius: "15px",
              textTransform: "none",
              px: "20px",
              flexShrink: 0,
              marginLeft: "auto",
              height: "30px",
              fontSize: "16px",
            }}
            onClick={() => {
              setIsNotesModalOpen(true);
            }}
          >
            Detector notes
          </Button>
        </Stack>
        {/* Left Panel */}
        <Grid
          item
          height="calc(100% - 50px)"
          width={{ sm: "250px", md: "350px" }}
          sx={{ display: ["none", "block"] }}
          className="overflow-y-hidden"
        >
          <Stack height="100%">
            <Box
              height="100%"
              sx={{
                border: "solid 1px #d6d8da",
                borderRadius: "10px",
              }}
            >
              <List
                dense
                sx={{
                  width: "100%",
                  height: "calc(100% - 40px)",
                  overflow: "auto",
                  padding: 0,
                  borderRadius: "10px 10px 0 0",
                }}
              >
                {rows?.map((row, index) => (
                  <QueryDetailsPreview
                    key={row?.iq_id}
                    rowData={row}
                    selectedId={posicheck?.iq_id || ""}
                    setSelectedRow={updateSelectedRowData}
                    setIsDeleteModalOpen={setIsDeleteModalOpen}
                    dispatch={localDispatch}
                    lastItem={index === rows.length - 1}
                  />
                ))}
              </List>
              <TablePagination
                component="div"
                count={rowCount}
                page={page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                rowsPerPageOptions={pageSize >= 25 ? [25, 50, 100] : []}
                backIconButtonProps={paginationButtonProps}
                nextIconButtonProps={paginationButtonProps}
                onRowsPerPageChange={(e) => {
                  if (setPageSize) {
                    setPageSize(Number(e.target.value));
                  }
                }}
                sx={{
                  "& .MuiToolbar-root": {
                    height: "40px",
                    minHeight: "40px",
                    borderTop: "solid 1px #d6d8da",
                    justifyItems: "center",
                    justifyContent: "space-between",
                    paddingLeft: 0,
                    width: "100%",
                  },
                  "& .MuiTablePagination-spacer": {
                    display: "none",
                  },
                  "& .MuiTablePagination-selectLabel": {
                    display: "none",
                  },
                  "& .MuiTablePagination-input": {
                    display: { xs: "none", sm: "none", md: "block" },
                  },
                  "& .MuiTablePagination-displayedRows": {
                    padding: "0 8px",
                  },
                  "& .MuiTablePagination-select.MuiTablePagination-select": {
                    paddingRight: 0,
                  },
                  "& .MuiButtonBase-root": {
                    height: "39px",
                  },
                  "& .MuiTablePagination-actions": {
                    display: isSinglePage ? "none" : "block",
                  },
                }}
              />
            </Box>
          </Stack>
        </Grid>
        {/* Right Panel */}
        <Grid item height="calc(100% - 50px)" width={{ sm: "calc(100% - 270px)", md: "calc(100% - 370px)" }}>
          <ImageQueryPanel
            data={updatedIQ}
            selectedLabelValue={labelSelected}
            bestPredictionLabelId={updatedIQ?.best_prediction_label_id}
            onSendLabel={onSendLabel}
            dataLabels={dataLabels}
            loadingLabels={loadingLabels}
            loading={loading || isRelabeling}
            detector={detector}
            onLabelSelected={(rowId) => {
              setSelectedLabelId(rowId);
            }}
            onUnsavedAnnotationsHandled={(confirmed) => {
              if (confirmed) {
                getData();
              }
              checkAnnotationsHoldReason();
            }}
          />
        </Grid>
      </Grid>
    ),
  };

  const initialStep = useInitialStep();
  const stepContent = initialStep ?? finalStep;

  const { useNextImageQueryHotkey, usePreviousImageQueryHotkey } = useQueryDetailsHotkeys({
    selectedRowIndex,
    rows,
    rowCount,
    pageSize,
    page,
    handleChangePage,
    updateSelectedRowIndex,
  });
  useNextImageQueryHotkey();
  usePreviousImageQueryHotkey();

  return (
    <>
      <ConfirmDeleteModal
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        modalTitle="DELETE IMAGE QUERY"
        onRemoveHandler={onDeleteIQHandler}
        isLoadingIQDelete={isLoadingIQDelete}
        modalContent="<strong>Deleting the image will mean that it will not be used to train the detector</strong><br />Are you sure you want to continue?"
      />
      <NotesModal
        detector={detector}
        openModal={isNotesModalOpen}
        ownerGroupId={detector.owner?.id || owner?.id}
        ownerGroupName={detector.owner?.name || owner?.name}
        onClose={() => {
          setIsNotesModalOpen(false);
        }}
      />
      <Modal
        body={stepContent.body}
        open={showDialog}
        onClose={stepContent.onCLose}
        size={stepContent.size}
        title={stepContent.title}
        titleAlignment="center"
      />
    </>
  );
});
